import { useCallback, useEffect, useState } from 'react'
import { breadcrumbList } from '../domain/breadcrumb'
import Container from '../../../../components/Container'
import { useToast } from 'hooks/toast'
import Form, { Select } from '../../../../components/Form'
import { FilterContainer } from './styles'
import { useHistory, useLocation } from 'react-router-dom'
import api from 'services/api'
import { apiListInstitution } from 'pages/Commercial/Contracts/domain/api'
import moment from 'moment'
import Button from 'components/Button'
import { currencyMask } from 'utlis/mask'
import { useLoading } from 'hooks/loading'
import Modal from 'components/Modal'

interface INfeSequence {
  lastNumber: number
  nextNumber: number
  fiscalSettingId: number
}

interface IFinancialMoviment {
  id: number
  contract_id: number
  due_date: string
  value: string
  approved_invoice_id: number | null
  invoice_data: any
  contract: {
    id: number
    client: { name: string }
    student: { name: string }
    institution: { company_social_name: string }
  }
  situation: number
  invoice_mail_sent: boolean | null
  invoice_mail_sent_at: string | null
  invoice_error?: string | null
  approvedInvoice?: {
    ide_dhEmi: string
  }
}

const QueueInvoices = (): JSX.Element => {
  const [institutionId, setInstitutionId] = useState<string>()
  const [period, setPeriod] = useState<string>()
  const [status, setStatus] = useState<string>()
  const [institutionOptions, setInstitutionOptions] = useState<
    IInstitutionData[]
  >([])
  const [parameters, setParameters] = useState<any[]>()
  const [periodOptions, setPeriodOptions] = useState<any[]>([])
  const [financialMoviments, setFinancialMoviments] = useState<any>()
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [nfeSequence, setNfeSequence] = useState<INfeSequence | null>(null)
  const [isEditSequenceModalOpen, setIsEditSequenceModalOpen] = useState(false)
  const [editNextNumber, setEditNextNumber] = useState<string>('')

  const { activeLoading, disableLoading, loading } = useLoading()
  const { addToast } = useToast()
  const history = useHistory()

  const getNfeSequence = useCallback(async () => {
    try {
      const response = await api.get<INfeSequence>(
        'financial/invoices/nfe-sequence'
      )
      setNfeSequence(response.data)
    } catch {
      addToast({
        type: 'error',
        title: 'Erro ao carregar numeração de NFe',
        description: 'Não foi possível carregar a numeração das notas fiscais.'
      })
    }
  }, [addToast])

  const handleOpenEditSequenceModal = () => {
    if (!nfeSequence) return
    setEditNextNumber(String(nfeSequence.nextNumber))
    setIsEditSequenceModalOpen(true)
  }

  const handleSaveNextNumber = useCallback(async () => {
    const nextNumber = Number(editNextNumber)

    if (!Number.isInteger(nextNumber) || nextNumber < 1) {
      addToast({
        type: 'error',
        title: 'Número inválido',
        description: 'Informe um número inteiro positivo.'
      })
      return
    }

    activeLoading()
    try {
      const response = await api.put<INfeSequence>(
        'financial/invoices/nfe-sequence',
        { nextNumber }
      )
      setNfeSequence(response.data)
      setIsEditSequenceModalOpen(false)
      addToast({
        type: 'success',
        title: 'Numeração atualizada com sucesso'
      })
    } catch (error: any) {
      addToast({
        type: 'error',
        title: 'Erro ao atualizar numeração',
        description:
          error?.response?.data?.message ||
          'Não foi possível atualizar a numeração da próxima nota fiscal.'
      })
    } finally {
      disableLoading()
    }
  }, [activeLoading, addToast, disableLoading, editNextNumber])

  const getFinancialMoviments = useCallback(
    async ({ institutionId, period, status }) => {
      activeLoading()
      try {
        const params = new URLSearchParams()
        if (institutionId) params.append('institution_id', institutionId)
        if (period) params.append('period', period)
        if (status) params.append('status', status)
        const response = await api.get(
          `financial/financialMoviments/listByPeriodAndInstitution?${params.toString()}`
        )
        setFinancialMoviments(response.data)
      } catch {
        addToast({
          type: 'error',
          title: 'Erro ao carregar movimentações financeiras',
          description:
            'Houve um erro ao carregar as movimentações financeiras para os parâmetros selecionados.'
        })
      } finally {
        disableLoading()
      }
    },
    [activeLoading, addToast, disableLoading]
  )

  useEffect(() => {
    getNfeSequence()
  }, [getNfeSequence])

  useEffect(() => {
    api
      .get(apiListInstitution())
      .then(res => {
        const orderedData = res.data
          .filter((institution: IInstitutionData) => institution.is_active)
          .sort((a: IInstitutionData, b: IInstitutionData) =>
            a.company_social_name.localeCompare(b.company_social_name)
          )
        setInstitutionOptions(orderedData)
      })
      .catch(() => {
        addToast({
          type: 'error',
          title: 'Erro ao carregar as instituições',
          description:
            'Houve um erro ao carregar as instituições disponíveis no banco de dados.'
        })
      })

    const period1 = `${String(moment().subtract(2, 'M').month() + 1).padStart(
      2,
      '0'
    )}/${moment().year()}`
    const period2 = `${String(moment().subtract(1, 'M').month() + 1).padStart(
      2,
      '0'
    )}/${moment().year()}`
    const period3 = `${String(moment().month() + 1).padStart(
      2,
      '0'
    )}/${moment().year()}`

    const months = [
      {
        name: period3,
        value: period3.split('/').reverse().join('-')
      },
      {
        name: period2,
        value: period2.split('/').reverse().join('-')
      },
      {
        name: period1,
        value: period1.split('/').reverse().join('-')
      }
    ].filter(m => m.name !== '02/2026') // Remover período específico, se necessário

    setPeriodOptions(months)
  }, [])

  const onSearch = async () => {
    if (!institutionId && !status) {
      addToast({
        type: 'error',
        title: 'Filtro obrigatório',
        description: 'Informe ao menos a instituição ou o status.'
      })
      return
    }

    const query = []
    if (institutionId) {
      query.push(`institution_id=${institutionId}`)
    }
    if (period) {
      query.push(`period=${period}`)
    }
    if (status) {
      query.push(`status=${status}`)
    }

    history.replace(`/financial/queueInvoices?${query.join('&')}`)
    setParameters([
      {
        institutionId: institutionId || undefined,
        period: period || undefined,
        status: status || undefined
      }
    ])
    await getFinancialMoviments({ institutionId, period, status })

    setSelectedIds([])
  }

  const location = new URLSearchParams(useLocation().search)

  useEffect(() => {
    const institutionId = location.get('institution_id')
    const period = location.get('period')
    const status = location.get('status')

    if (institutionId || period || status) {
      if (institutionId) {
        setInstitutionId(institutionId)
      }
      if (period) {
        setPeriod(period)
      }
      if (status) {
        setStatus(status)
      }
      setParameters([
        {
          institutionId: institutionId || undefined,
          period: period || undefined,
          status: status || undefined
        }
      ])
      getFinancialMoviments({ institutionId, period, status })
    } else {
      setParameters(undefined)
    }
  }, [getFinancialMoviments])

  const moviments: IFinancialMoviment[] =
    (financialMoviments as IFinancialMoviment[]) || []

  const selectableIds = moviments
    .filter(
      m => m.invoice_mail_sent !== false && m.approved_invoice_id === null
    )
    .map(m => m.id)

  const allSelected =
    selectableIds.length > 0 &&
    selectableIds.every(id => selectedIds.includes(id))

  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedIds([])
    } else {
      setSelectedIds(selectableIds)
    }
  }

  const handleToggleSelect = (id: number) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(v => v !== id) : [...prev, id]
    )
  }

  const handlePrintDanfe = useCallback(
    async ({
      financial_moviment_id,
      contract_id,
      client_name,
      period: movPeriod
    }: {
      financial_moviment_id: number
      contract_id: number
      client_name: string
      period: string
    }) => {
      activeLoading()
      try {
        const response = await api.get(
          `/financial/invoices/danfe/download/${financial_moviment_id}`,
          { responseType: 'blob' }
        )
        const contentDisposition = response.headers['content-disposition']
        const fileNameMatch = contentDisposition?.match(
          /filename\*?=(?:UTF-8''|")?([^";]+)/i
        )
        const fileName = fileNameMatch?.[1]
          ? decodeURIComponent(fileNameMatch[1].replace(/"/g, '').trim())
          : `danfe-${contract_id}-${String(client_name)
              .replaceAll(' ', '_')
              .toUpperCase()}-${movPeriod}.pdf`
        const blob =
          response.data instanceof Blob
            ? response.data
            : new Blob([response.data], { type: 'application/pdf' })
        const url = window.URL.createObjectURL(blob)
        const anchor = document.createElement('a')
        anchor.href = url
        anchor.download = fileName
        document.body.appendChild(anchor)
        anchor.click()
        anchor.remove()
        window.URL.revokeObjectURL(url)
        disableLoading()
        addToast({ type: 'success', title: 'DANFE baixada com sucesso' })
      } catch {
        disableLoading()
        addToast({ type: 'error', title: 'Erro ao baixar DANFE' })
      }
    },
    [activeLoading, addToast, disableLoading]
  )

  const handleDownloadXml = useCallback(
    async ({
      financial_moviment_id,
      contract_id,
      client_name,
      period: movPeriod
    }: {
      financial_moviment_id: number
      contract_id: number
      client_name: string
      period: string
    }) => {
      activeLoading()
      try {
        const response = await api.get(
          `/financial/invoices/xml/download/${financial_moviment_id}`,
          { responseType: 'blob' }
        )
        const contentDisposition = response.headers['content-disposition']
        const fileNameMatch = contentDisposition?.match(
          /filename\*?=(?:UTF-8''|")?([^";]+)/i
        )
        const fileName = fileNameMatch?.[1]
          ? decodeURIComponent(fileNameMatch[1].replace(/"/g, '').trim())
          : `xml-nfe-${contract_id}-${String(client_name)
              .replaceAll(' ', '_')
              .toUpperCase()}-${movPeriod}.xml`
        const blob =
          response.data instanceof Blob
            ? response.data
            : new Blob([response.data], { type: 'application/xml' })
        const url = window.URL.createObjectURL(blob)
        const anchor = document.createElement('a')
        anchor.href = url
        anchor.download = fileName
        document.body.appendChild(anchor)
        anchor.click()
        anchor.remove()
        window.URL.revokeObjectURL(url)
        disableLoading()
        addToast({ type: 'success', title: 'XML baixado com sucesso' })
      } catch {
        disableLoading()
        addToast({ type: 'error', title: 'Erro ao baixar XML' })
      }
    },
    [activeLoading, addToast, disableLoading]
  )

  const handleQueueInvoices = useCallback(async () => {
    if (selectedIds.length === 0) return
    activeLoading()
    try {
      await api.post('financial/financialMoviments/setInvoiceMailSent', {
        financialMoviments: selectedIds
      })
      addToast({ type: 'success', title: 'Movimentações adicionadas à fila' })
      setSelectedIds([])
      await getFinancialMoviments({ institutionId, period, status })
    } catch {
      addToast({ type: 'error', title: 'Erro ao adicionar à fila' })
    }
    disableLoading()
  }, [
    selectedIds,
    activeLoading,
    disableLoading,
    addToast,
    getFinancialMoviments,
    institutionId,
    period,
    status
  ])

  return (
    <Container
      pageTitle="Emissão de notas fiscais"
      breadcrumb={breadcrumbList}
      tools={[]}
    >
      <FilterContainer>
        <Form onSubmit={onSearch} defaultValues={{}} className="col-md-12">
          <div className="row">
            <div className="col-md-2 pe-8">
              <Select
                label="Periodo"
                name="period"
                options={periodOptions}
                onChange={e => setPeriod(e.target.value)}
                value={period}
                controlled
                blank
                defaultValue={''}
              />
            </div>
            <div className="col-md-4 pe-8">
              <Select
                label="Instituição"
                name="institution_id"
                options={institutionOptions?.map(institution => {
                  return {
                    name: institution.company_social_name,
                    value: institution.id
                  }
                })}
                onChange={e => setInstitutionId(e.target.value)}
                value={institutionId}
                blank
                controlled
                defaultValue={''}
              />
            </div>
            <div className="col-md-2">
              <Select
                label="Status"
                name="status"
                options={[
                  { name: 'Todos', value: 'all' },
                  { name: 'Emitida', value: 'issued' },
                  { name: 'Falhou', value: 'failed' }
                ]}
                onChange={e => setStatus(e.target.value)}
                value={status}
                controlled
                blank
                defaultValue={''}
              />
            </div>
          </div>
          {nfeSequence && (
            <div className="row mb-5 mt-5">
              <div className="col-md-12 d-flex flex-wrap gap-5 align-items-center">
                <span>
                  <strong>Última nota fiscal emitida:</strong>{' '}
                  {nfeSequence.lastNumber > 0 ? nfeSequence.lastNumber : '—'}
                </span>
                <span className="d-flex align-items-center gap-2">
                  <strong>Próxima nota fiscal:</strong> {nfeSequence.nextNumber}
                  <span
                    className="fa fa-edit cursor-pointer text-primary"
                    title="Alterar próxima nota fiscal"
                    role="button"
                    tabIndex={0}
                    onClick={handleOpenEditSequenceModal}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        handleOpenEditSequenceModal()
                      }
                    }}
                  />
                </span>
              </div>
            </div>
          )}
          <footer className="col-md-12 d-flex justify-content-end pb-5 gap-5">
            <button
              type="reset"
              className="btn btn-secondary btn-sm"
              onClick={() => {
                setInstitutionId(undefined)
                setPeriod(undefined)
                setStatus(undefined)
              }}
            >
              LIMPAR
            </button>
            <button type="submit" className="btn btn-primary btn-sm">
              BUSCAR
            </button>
          </footer>
        </Form>
        <hr />
      </FilterContainer>
      {parameters && (
        <div>
          <h3 className="mt-5">Pagamentos ({moviments?.length || 0})</h3>
          <div
            className="table-responsive mt-3"
            style={{ maxHeight: '60vh', overflowY: 'auto' }}
          >
            <table className="table table-bordered table-hover align-middle">
              <thead
                className="table-light"
                style={{ position: 'sticky', top: 0, zIndex: 1 }}
              >
                <tr>
                  <th style={{ width: 40 }}>
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={handleSelectAll}
                      disabled={selectableIds.length === 0}
                    />
                  </th>
                  <th>#</th>
                  <th>Instituição</th>
                  <th>Contrato</th>
                  <th>Cliente</th>
                  <th>Aluno</th>
                  <th>Vencimento</th>
                  <th>Status Pagamento</th>
                  <th>Valor</th>
                  <th>Emissão da nota</th>
                  <th>Emitida em</th>
                  <th>Enviado em</th>
                  <th>Erro</th>
                  <th>Fiscal</th>
                </tr>
              </thead>
              <tbody>
                {moviments.map((m, index) => {
                  const isDisabled =
                    m.invoice_mail_sent !== null ||
                    m.approved_invoice_id !== null
                  const isChecked = selectedIds.includes(m.id)
                  return (
                    <tr key={m.id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={isChecked}
                          disabled={isDisabled}
                          onChange={() => handleToggleSelect(m.id)}
                        />
                      </td>
                      <td>{index + 1}</td>
                      <td>{m.contract?.institution?.company_social_name}</td>
                      <td>
                        <a
                          href={`/commercial/contracts/view/${m.contract_id}`}
                        >{`${m.contract_id}`}</a>
                      </td>
                      <td>{m.contract?.client?.name}</td>
                      <td>{m.contract?.student?.name}</td>
                      <td>{m.due_date?.substring(0, 10)}</td>
                      <td>{m.situation === 1 ? 'Pago' : 'Pendente'}</td>
                      <td>{currencyMask(m.value)}</td>
                      <td
                        className={`text-${
                          m.approved_invoice_id !== null ? 'success' : 'warning'
                        }`}
                      >
                        {m.invoice_mail_sent === false
                          ? 'Na fila'
                          : m.approved_invoice_id !== null
                          ? 'Emitida'
                          : '—'}
                      </td>
                      <td>
                        {m.approvedInvoice?.ide_dhEmi
                          ? moment(m.approvedInvoice.ide_dhEmi).format(
                              'DD/MM/YYYY HH:mm:ss'
                            )
                          : '—'}
                      </td>
                      <td>{m.invoice_mail_sent_at || '—'}</td>
                      <td title={m.invoice_error || undefined}>
                        {m.invoice_error
                          ? m.invoice_error.length > 15
                            ? `${m.invoice_error.substring(0, 15)}...`
                            : m.invoice_error
                          : '—'}
                      </td>
                      <td>
                        {m.approved_invoice_id !== null && (
                          <div className="d-flex gap-2">
                            <button
                              type="button"
                              className="btn btn-sm btn-light-primary"
                              title="Baixar DANFE"
                              onClick={() =>
                                handlePrintDanfe({
                                  financial_moviment_id: m.id,
                                  contract_id: m.contract_id,
                                  client_name: m.contract?.client?.name,
                                  period: period || ''
                                })
                              }
                            >
                              DANFE
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-light-success"
                              title="Baixar XML"
                              onClick={() =>
                                handleDownloadXml({
                                  financial_moviment_id: m.id,
                                  contract_id: m.contract_id,
                                  client_name: m.contract?.client?.name,
                                  period: period || ''
                                })
                              }
                            >
                              XML
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          <div className="d-flex justify-content-end mt-5">
            <Button
              className="btn btn-primary btn-sm"
              onClick={handleQueueInvoices}
              disabled={loading || selectedIds.length === 0}
            >
              {`Emitir (${selectedIds.length})`}
            </Button>
          </div>
        </div>
      )}
      {isEditSequenceModalOpen && (
        <Modal
          pageTitle="Alterar próxima nota fiscal"
          isOpenModal={isEditSequenceModalOpen}
          onClickButtonCancel={() => setIsEditSequenceModalOpen(false)}
          Children={
            <div className="p-4">
              <div className="mb-4">
                <label htmlFor="nextNumber" className="form-label">
                  Número da próxima nota fiscal
                </label>
                <input
                  id="nextNumber"
                  type="number"
                  min={1}
                  className="form-control"
                  value={editNextNumber}
                  onChange={e => setEditNextNumber(e.target.value)}
                />
              </div>
              <div className="d-flex justify-content-end gap-3">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setIsEditSequenceModalOpen(false)}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={handleSaveNextNumber}
                  disabled={loading}
                >
                  Salvar
                </button>
              </div>
            </div>
          }
        />
      )}
    </Container>
  )
}

export default QueueInvoices
